// ── #675 / #608, GATE 675 D1-D4 AND D6, Session 79.22b.5.4 -- FINDING A
// TRANSFER BY TYPING ──────────────────────────────────────────────────────────
//
// What a person sees: one search box above the Transfers tabs. Dallas's
// manager takes a call about HOU-TRF-00036, types "hou trf 36", and every tab's
// count turns into how many matches that tab holds - Finished included - so the
// count says where to look. A truck number off a load sheet, a work order or an
// item name finds the transfer the same way, and a card says what matched when
// the match is not printed on its face ("Matched: truck HOU-TRF-00036-2").
//
// THE MATCHING IS NOT HERE, AND MUST NEVER BE COPIED HERE. It lives in the
// database, in one rule for every tab (D5, find_branch_transfers(), built at
// 79.22b.5.3 in db/migrations/20261003_find_branch_transfers_675.sql), so a
// search can never find something on To load that the same words miss on
// Finished - which two matchers eventually would (#358's lesson). This file
// asks the question, reads the answer, and holds the gate's exact words.
//
// One thing the screen does need to know is whether a search happened at all.
// The database answers a term with nothing usable in it - "WO", "--" - as NO
// SEARCH, returning every row with nothing marked as matched. readSearchRows()
// reads that from the answer itself rather than re-deriving the rule.

import { supabase } from './supabase';

// D4 -- it starts at two characters. One character does nothing and says
// nothing (F2, "Too short"), and the list stays as it is.
export const MIN_SEARCH_CHARS = 2;
// F3 -- about a third of a second after typing stops, or at once on Enter.
export const SEARCH_PAUSE_MS = 300;
// At most this many finished matches at OTHER branches are listed (D2). A
// search for a number finds one; a word like "stake" at four branches finds a
// list, and the line under it says to type more of the number.
export const OTHER_BRANCH_CAP = 25;
// How many finished matches touching the other branches are READ to find those
// 25. A transfer touching the branch in view is also "at another branch" by the
// database's rule (its other end is), so the read must be wide enough to drop
// those first and still fill the list - asking for 25 and then dropping left
// the list short, or empty, with nothing saying so (Grok's review, 79.22b.5.4).
// 1,000 is the database's own ceiling for one answer.
export const OTHER_BRANCH_READ = 1000;
// Rows read back by id in groups, so the address of one read stays short.
const ID_CHUNK = 200;

// F2, the copy table, word for word.
export const SEARCH_PLACEHOLDER_PHONE = 'Search transfers';
export const SEARCH_PLACEHOLDER_DESK = 'Search transfer, truck, work order or item';
export const SEARCH_ARIA_LABEL = 'Search transfers by transfer, truck, work order or request number, or by item';
export const CLEAR_SEARCH = 'Clear search';
export const SEARCHING = 'Searching…';
export const SEARCH_SCOPE = 'Searches transfer and truck numbers, work orders, the request a transfer came from, and item names — on every tab and every finished transfer.';
export const SEARCH_FAILED = 'Couldn’t search just now. Check the connection and try again.';
export const TRY_AGAIN = 'Try again';
export const AT_OTHER_BRANCHES = 'At other branches';

// What is worth sending to the database at all: the term trimmed (a pasted
// number with trailing spaces), at least two characters, and at least one
// letter or digit - "--" is nothing to look for (X2). Anything finer ("WO"
// alone) is the database's call, read back by readSearchRows().
export function searchableTerm(raw) {
  const t = String(raw ?? '').trim();
  if (t.length < MIN_SEARCH_CHARS) return null;
  if (!/[A-Za-z0-9]/.test(t)) return null;
  return t;
}

// D3 / F2 -- why a card matched, when the match is not on its face. A match on
// the transfer number or an item prints nothing, because the card already
// shows both.
export function matchLine(match) {
  const v = match?.matchedValue;
  if (!v) return null;
  switch (match.matchedOn) {
    case 'truck': return `Matched: truck ${v}`;
    case 'work_order': return `Matched: work order ${v}`;
    case 'request': return `Matched: request ${v}`;
    default: return null;
  }
}

function rowToMatch(r) {
  return {
    transferId: r.transfer_id,
    loadId: r.load_id ?? null,
    matchedOn: r.matched_on || null,
    matchedValue: r.matched_value || null,
  };
}

// The database's answer, read. Any row with nothing marked as matched means the
// term held nothing usable and the database treated it as no search at all, so
// the screen shows its lists as they are. Otherwise: transfer id -> what matched.
export function readSearchRows(rows) {
  const list = rows || [];
  if (list.some(r => !r.matched_on)) return { usable: false, matches: new Map() };
  return { usable: true, matches: new Map(list.map(r => [String(r.transfer_id), rowToMatch(r)])) };
}

function rpc(args) {
  return supabase.rpc('find_branch_transfers', args);
}

// ONE SEARCH, THREE QUESTIONS, side by side:
//   open      every match in the work in hand, at every branch the reader may
//             look at (`scopeIds`: null for every branch; a team member's own
//             branch only - D2, M2). The page holds the work in hand already,
//             so a match at another branch is resolved from what it holds.
//   finished  how many finished transfers in VIEW match (`viewIds`, null for
//             every branch) - Finished's count while a search is on (D1).
//   others    finished matches at the OTHER branches the reader may look at
//             (`otherIds`), for D2's At other branches list. Skipped when the
//             reader sees every branch or may look nowhere else. A transfer
//             touching the branch in view is in view, never "other", so those
//             are dropped here.
// `finishedFilters` (gate 675 D9, D11, Session 79.22b.5.4b): Finished's count
// while a search is on is the count of what Finished would list - the same
// narrowing its page is read with (finishedArgs() in transferSortFilter.js).
export async function searchTransfers({ term, scopeIds = null, viewIds = null, otherIds = null, finishedFilters = null }) {
  const askOthers = Array.isArray(otherIds) && otherIds.length > 0;
  const [open, fin, oth] = await Promise.all([
    rpc({ p_scope: 'open', p_branch_ids: scopeIds, p_term: term }),
    rpc({ p_scope: 'finished', p_branch_ids: viewIds, p_term: term, p_limit: 1, ...(finishedFilters || {}) }),
    askOthers
      ? rpc({ p_scope: 'finished', p_branch_ids: otherIds, p_term: term, p_limit: OTHER_BRANCH_READ })
      : Promise.resolve({ data: [], error: null }),
  ]);
  const error = open.error || fin.error || oth.error;
  if (error) return { error };

  const openRead = readSearchRows(open.data);
  const finRead = readSearchRows(fin.data);
  const othRead = readSearchRows(oth.data);
  if (!openRead.usable || !finRead.usable || !othRead.usable) {
    return { term, usable: false, open: new Map(), finishedTotal: 0, otherFinished: [], otherFinishedMore: false, error: null };
  }

  const finishedTotal = (fin.data || []).length ? Number(fin.data[0].total_count) || 0 : 0;

  let otherFinished = [];
  let otherFinishedMore = false;
  const othRows = oth.data || [];
  if (othRows.length) {
    const ids = othRows.map(r => r.transfer_id);
    const chunks = [];
    for (let i = 0; i < ids.length; i += ID_CHUNK) chunks.push(ids.slice(i, i + ID_CHUNK));
    const reads = await Promise.all(chunks.map(c => supabase
      .from('branch_transfers')
      .select('id, transfer_number, from_branch_id, to_branch_id, status, fulfilment_mode')
      .in('id', c)));
    const rowsErr = reads.find(x => x.error)?.error;
    if (rowsErr) return { error: rowsErr };
    const inView = new Set((viewIds || []).map(String));
    const byId = new Map(reads.flatMap(x => x.data || []).map(t => [String(t.id), t]));
    // In the database's order (most recently finished first), those touching
    // the branch in view dropped BEFORE the list is cut to its 25.
    const kept = othRows
      .map(r => byId.get(String(r.transfer_id)))
      .filter(t => t && !inView.has(String(t.from_branch_id)) && !inView.has(String(t.to_branch_id)));
    otherFinished = kept.slice(0, OTHER_BRANCH_CAP)
      .map(t => ({ ...t, __match: othRead.matches.get(String(t.id)) || null }));
    // More than are listed: more kept than shown, or the read itself was cut
    // at the database's ceiling, so some may not have been looked at.
    otherFinishedMore = kept.length > OTHER_BRANCH_CAP
      || (Number(othRows[0].total_count) || 0) > othRows.length;
  }

  return { term, usable: true, open: openRead.matches, finishedTotal, otherFinished, otherFinishedMore, error: null };
}

// ── WHAT A SEARCH LEAVES ON EACH TAB ────────────────────────────────────────
//
// A card kept in place after a press (#665 D21) stays whatever the search says:
// it was on screen when the press happened, and its answer and Undo live on it.

// A list of transfers, or of rows that each stand for one (`idOf` says which).
export function keepMatching(list, idOf, matches) {
  return (list || []).filter(x => x?.__pinned || x?.pinned || matches.has(String(idOf(x))));
}

// A list of TRUCKS, where a card is one truck. A search that matched a truck
// number keeps that truck and not its sister trucks; a search that matched the
// transfer keeps all of them. Used on On the way and To count in. To collect
// keeps a pile by its transfer, because a pile card stands for whatever
// trucks left the goods there.
export function keepMatchingTrucks(loads, matches) {
  return (loads || []).filter(l => {
    if (l?.__pinned) return true;
    const m = matches.get(String(l.transfer_id));
    if (!m) return false;
    return m.loadId == null || String(m.loadId) === String(l.id);
  });
}

// D6 -- FOUND ELSEWHERE. "Nothing on Awaiting confirmation matches. 1 on To
// count in." `others` are the tabs holding matches, in tab order, as
// { label, count }. A page that jumps tabs by itself is one the reader stops
// trusting, so this is a signpost with a button per tab, never a move.
export function foundElsewhereSentence(tabLabel, others = []) {
  const head = `Nothing on ${tabLabel} matches.`;
  if (!others.length) return head;
  return `${head} ${others.map(o => `${o.count} on ${o.label}`).join(', ')}.`;
}

// D6 -- FOUND NOWHERE, with the term in curly quotes as the copy table has it.
export function notFoundSentence(term) {
  return `No transfer matches “${term}”.`;
}

// D2 -- WHICH BRANCH "Look at" switches to, for a match at another branch:
// the reader's own branch when the transfer touches it (Jennifer at Houston,
// looking at Dallas, finding a Houston-to-Austin transfer), otherwise the
// branch that asked for it.
export function lookAtBranchFor(transfer, myBranch) {
  if (!transfer) return null;
  if (myBranch != null && (String(transfer.from_branch_id) === String(myBranch)
    || String(transfer.to_branch_id) === String(myBranch))) return myBranch;
  return transfer.to_branch_id ?? transfer.from_branch_id ?? null;
}

// F1 -- A SLOW REPLY NEVER REPLACES A NEWER ONE. Each search takes a ticket;
// only the newest ticket's reply is applied.
export function latestOnly() {
  let newest = 0;
  return {
    next: () => { newest += 1; return newest; },
    isLatest: n => n === newest,
  };
}
